import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getClients, getServices, createDeliveryNote } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { ArrowLeft, Plus, Trash2, Save } from 'lucide-react';
import { toast } from 'sonner';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { useGrouping: 'always', style: 'currency', currency: 'EUR' }).format(amount);
};

export const DeliveryNoteFormPage = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    client_id: '',
    delivery_number: '',
    issue_date: new Date().toISOString().split('T')[0],
    items: [{ description: '', quantity: 1, unit_price: 0, tax_rate: 21 }],
    notes: '',
    status: 'draft'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsRes, servicesRes] = await Promise.all([getClients(), getServices()]);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
    } catch (e) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const addItem = () => {
    setFormData({
      ...formData,
      items: [...formData.items, { description: '', quantity: 1, unit_price: 0, tax_rate: 21 }]
    });
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData({ ...formData, items: newItems });
  };

  const addServiceToItems = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    if (service) {
      setFormData({
        ...formData,
        items: [
          ...formData.items.filter(item => item.description),
          { service_id: service.id, description: service.name, quantity: 1, unit_price: service.hourly_rate, tax_rate: 21 }
        ]
      });
    }
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => {
      const subtotal = item.quantity * item.unit_price;
      return sum + subtotal + (subtotal * item.tax_rate / 100);
    }, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.client_id) {
      toast.error('Selecciona un cliente');
      return;
    }

    setSaving(true);
    try {
      const data = {
        ...formData,
        items: formData.items.filter(item => item.description).map(item => ({
          ...item,
          quantity: parseFloat(item.quantity) || 0,
          unit_price: parseFloat(item.unit_price) || 0,
          tax_rate: parseFloat(item.tax_rate) || 21
        }))
      };
      await createDeliveryNote(data);
      toast.success('Albarán creado');
      navigate('/delivery-notes');
    } catch (e) {
      toast.error('Error al crear albarán');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/delivery-notes')} className="p-2 hover:bg-stone-100 rounded-xl">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-2xl font-heading font-extrabold tracking-tight">Nuevo Albarán</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6">
          <h2 className="text-lg font-heading font-bold mb-4">Datos Básicos</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label>Cliente *</Label>
              <Select value={formData.client_id || undefined} onValueChange={(v) => setFormData({ ...formData, client_id: v })}>
                <SelectTrigger className="rounded-xl" data-testid="delivery-client-select">
                  <SelectValue placeholder="Selecciona cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Número de Albarán</Label>
              <Input
                value={formData.delivery_number}
                onChange={(e) => setFormData({ ...formData, delivery_number: e.target.value })}
                className="rounded-xl font-mono"
                placeholder="Auto"
                data-testid="delivery-number-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input
                type="date"
                value={formData.issue_date}
                onChange={(e) => setFormData({ ...formData, issue_date: e.target.value })}
                className="rounded-xl"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
            <h2 className="text-lg font-heading font-bold">Conceptos</h2>
            {services.length > 0 && (
              <Select onValueChange={addServiceToItems}>
                <SelectTrigger className="w-full sm:w-48 rounded-xl"><SelectValue placeholder="Añadir servicio" /></SelectTrigger>
                <SelectContent>
                  {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>

          <div className="space-y-4">
            {formData.items.map((item, index) => (
              <div key={index} className="border border-stone-100 rounded-xl p-3 md:p-0 md:border-0 md:rounded-none">
                <div className="hidden md:grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-5">
                    <Input value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} placeholder="Descripción" className="rounded-xl" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="rounded-xl font-mono" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', e.target.value)} className="rounded-xl font-mono" />
                  </div>
                  <div className="col-span-2">
                    <Input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', e.target.value)} className="rounded-xl font-mono" />
                  </div>
                  <div className="col-span-1">
                    <button type="button" onClick={() => removeItem(index)} className="p-1.5 hover:bg-red-50 rounded-xl" disabled={formData.items.length === 1}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="md:hidden space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-xs text-stone-500 font-medium">Concepto {index + 1}</span>
                    <button type="button" onClick={() => removeItem(index)} className="p-1 hover:bg-red-50 rounded-xl" disabled={formData.items.length === 1}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                  <Input value={item.description} onChange={(e) => updateItem(index, 'description', e.target.value)} placeholder="Descripción" className="rounded-xl" />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <span className="text-xs text-stone-500">Cant.</span>
                      <Input type="number" value={item.quantity} onChange={(e) => updateItem(index, 'quantity', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                    <div>
                      <span className="text-xs text-stone-500">Precio</span>
                      <Input type="number" value={item.unit_price} onChange={(e) => updateItem(index, 'unit_price', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                    <div>
                      <span className="text-xs text-stone-500">IVA %</span>
                      <Input type="number" value={item.tax_rate} onChange={(e) => updateItem(index, 'tax_rate', e.target.value)} className="rounded-xl font-mono" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
            <Button type="button" variant="outline" onClick={addItem} className="rounded-xl">
              <Plus className="w-4 h-4 mr-2" />Añadir
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t flex justify-end">
            <div className="text-lg font-bold">
              Total: <span className="font-mono text-[#FF6B4A]">{formatCurrency(calculateTotal())}</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-6">
          <h2 className="text-lg font-heading font-bold mb-4">Notas</h2>
          <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Notas..." className="rounded-xl resize-none" rows={3} />
        </div>

        <div className="flex gap-4 justify-end">
          <Button type="button" variant="outline" onClick={() => navigate('/delivery-notes')} className="rounded-xl">Cancelar</Button>
          <Button type="submit" disabled={saving} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="save-delivery-note-button">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Guardando...' : 'Crear Albarán'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DeliveryNoteFormPage;
